import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store/store";
import { parseURLParams } from "@/utils/url";
import { setPendingSelectionIfChanged } from "@/store/slices/pendingSelection/pendingSelectionSlice";

/**
 * Fires exactly once after bootstrap data (brands + markets) is ready.
 *
 * Reads the current URL search params, derives the module from the first
 * pathname segment, and dispatches `setPendingSelectionIfChanged` with that payload.
 *
 * From there the full Pending Selection → RTK Listener → merge + validate →
 * update URL → useURLToStore → syncFromURL → userSelection flow takes over
 * automatically.
 *
 * Must be rendered inside the Router tree (requires `useLocation`).
 */
export const useInitialURLBootstrap = (): void => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const hasBootstrapped = useRef(false);

  const isDataFetched = useSelector(
    (state: RootState) => state.activeGroup.bootstrapDataStatus.isDataFetched,
  );

  useEffect(() => {
    if (!isDataFetched || hasBootstrapped.current) return;

    // Derive module from the first pathname segment (e.g. "/equity/brand" → "equity")
    const module = location.pathname.split("/")?.[1] ?? "";

    // Wait until Home.tsx has redirected away from "/" before bootstrapping.
    // If we fire while still on "/" the module will be "" and the
    // selectionListener will find no config → early-return without navigating.
    if (!module) return;

    // Guard: fire exactly once (after the pathname is a real module path)
    hasBootstrapped.current = true;

    const searchParams = new URLSearchParams(location.search);
    const parsed = parseURLParams(searchParams);

    // `parsed.filters`, if present, is now a structured `FilterSelection[]`
    // produced by the v2 `fv=2` + `f=` URL grammar (spec §6.5.1) — pass it
    // through to `setPendingSelectionIfChanged` unmodified via the `...parsed`
    // spread below. A legacy numeric-id `filters` param on the initial-load
    // URL (no `fv=2`) produces no `filters` key at all (see `parseURLParams`),
    // so there is nothing here to strip.

    dispatch(
      setPendingSelectionIfChanged({
        ...parsed,
        module,
      } as any),
    );
  }, [isDataFetched, location.pathname]);
};

export default useInitialURLBootstrap;